"use client";

import { useEffect, useState } from "react";
import {
  CalendarPlus,
  HelpCircle,
  Loader2,
  Menu,
  Pause,
  Play,
  Search,
  UserRound,
  type LucideIcon,
} from "lucide-react";

import { useBookingDrawer } from "@/lib/booking-context";
import { useFaqDrawer } from "@/lib/faq-context";
import { useMenuDrawer } from "@/lib/menu-context";
import { setMiniDrawerOpen } from "@/lib/podcast-mini-drawer";
import { usePodcastActive } from "@/lib/podcast-now-playing";
import { PORTAL_URL } from "@/lib/region";
import { useSearch } from "@/lib/search-context";
import { seasonHex } from "@/components/podcast/podcast-cover";
import { usePlayerState } from "@/components/podcast/podcast-player";

function DockLink({
  href,
  label,
  Icon,
  active,
}: {
  href: string;
  label: string;
  Icon: LucideIcon;
  active: boolean;
}) {
  return (
    <a
      href={href}
      aria-current={active ? "page" : undefined}
      className="flex h-full w-16 items-center justify-center"
    >
      <span
        className={`flex size-12 flex-col items-center justify-center gap-1 rounded-full transition-colors duration-200 ${
          active
            ? "bg-white/15 text-white"
            : "text-white/75 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Icon className="size-5" strokeWidth={1.75} />
        <span className="text-caption font-semibold leading-none tracking-wide">
          {label}
        </span>
      </span>
    </a>
  );
}

export default function Dock() {
  const { setOpen: setBookingOpen } = useBookingDrawer();
  const { setOpen: setFaqOpen } = useFaqDrawer();
  const { setOpen: setMenuOpen } = useMenuDrawer();
  const { setOpen: setSearchOpen } = useSearch();
  const [visible, setVisible] = useState(false);
  const [bookMode, setBookMode] = useState(false);
  // /podcast page uses its own sticky bar; elsewhere keep the dock pinned when audio is loaded.
  const podcastActive = usePodcastActive();
  const playerState = usePlayerState();
  const hasEpisode = Boolean(playerState.episode);
  const shown = (visible || hasEpisode) && !podcastActive;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onScroll = () => {
      const nextVisible = window.scrollY > 40;
      setVisible(nextVisible);
      if (nextVisible) {
        if (!timer) {
          timer = setTimeout(() => {
            setBookMode(true);
            timer = null;
          }, 4000);
        }
      } else {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        setBookMode(false);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    // The fixed wrapper carries no transform — a transform here breaks
    // `position: fixed` on iOS Safari (the bar scrolls with the page). The
    // slide/fade animation lives on the inner element instead.
    <div
      data-floating-dock
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 pb-[max(1rem,env(safe-area-inset-bottom))] md:hidden"
      aria-label="Primary"
      aria-hidden={!shown}
      inert={!shown}
    >
      <div
        className={`mx-auto max-w-md px-5 transition-all duration-300 ${
          shown ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        }`}
      >
        <nav aria-label="Quick links" className="pointer-events-auto relative flex h-[68px] items-center justify-between rounded-full bg-forest-03/30 px-2 text-white shadow-[0_20px_48px_-12px_rgba(0,0,0,0.45)] ring-1 ring-white/15 backdrop-blur-2xl backdrop-saturate-150">
          <div className="flex">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="flex h-full w-16 items-center justify-center"
              aria-label="Open menu"
            >
              <span className="flex size-12 flex-col items-center justify-center gap-1 rounded-full text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white">
                <Menu className="size-5" strokeWidth={1.75} />
                <span className="text-caption font-semibold leading-none tracking-wide">
                  Menu
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex h-full w-16 items-center justify-center"
              aria-label="Search"
            >
              <span className="flex size-12 flex-col items-center justify-center gap-1 rounded-full text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white">
                <Search className="size-5" strokeWidth={1.75} />
                <span className="text-caption font-semibold leading-none tracking-wide">
                  Search
                </span>
              </span>
            </button>
          </div>
          <div className="flex">
            <DockLink href={PORTAL_URL} label="Portal" Icon={UserRound} active={false} />
            <button
              type="button"
              onClick={() => setFaqOpen(true, "mobile-dock")}
              className="flex h-full w-16 items-center justify-center"
              aria-label="Open FAQ"
            >
              <span className="flex size-12 flex-col items-center justify-center gap-1 rounded-full text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white">
                <HelpCircle className="size-5" strokeWidth={1.75} />
                <span className="text-caption font-semibold leading-none tracking-wide">
                  FAQ
                </span>
              </span>
            </button>
          </div>

          {hasEpisode ? (
            <PodcastDockButton
              seasonNumber={playerState.seasonNumber}
              playing={playerState.playing}
              loading={playerState.loading}
              progressPct={
                (() => {
                  const dur =
                    playerState.duration > 0
                      ? playerState.duration
                      : playerState.episode?.duration ?? 0;
                  return dur > 0
                    ? (playerState.currentTime / dur) * 100
                    : 0;
                })()
              }
            />
          ) : (
            <button
              type="button"
              onClick={() => setBookingOpen(true, "mobile-dock")}
              aria-label="Book Consultation"
              className="absolute top-0 left-1/2 flex size-16 -translate-x-1/2 -translate-y-[25px] items-center justify-center rounded-full bg-purple-03 text-white shadow-[0_12px_28px_-6px_rgba(106,95,218,0.55)] ring-4 ring-white/20 transition-transform hover:scale-105 active:scale-95"
            >
              <span
                aria-hidden
                className={`pointer-events-none absolute inset-0 rounded-full transition-opacity duration-300 ${
                  bookMode ? "opacity-0" : "opacity-100"
                }`}
                style={{
                  animation: bookMode
                    ? "none"
                    : "dock-halo 1.8s ease-out infinite",
                }}
              />
              <span className="relative flex size-full items-center justify-center">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/assets/logo/icon.svg"
                  alt=""
                  aria-hidden
                  className={`absolute h-[22px] w-auto transition-all duration-500 ease-out ${
                    bookMode ? "scale-90 opacity-0" : "scale-100 opacity-100"
                  }`}
                />
                <span
                  className={`absolute flex flex-col items-center justify-center gap-0.5 transition-all duration-500 ease-out ${
                    bookMode
                      ? "translate-y-0 scale-100 opacity-100"
                      : "translate-y-1 scale-95 opacity-0"
                  }`}
                >
                  <CalendarPlus className="size-5" strokeWidth={1.75} />
                  <span className="text-caption font-bold leading-none tracking-wide">
                    Book
                  </span>
                </span>
              </span>
            </button>
          )}
        </nav>
      </div>
    </div>
  );
}

function PodcastDockButton({
  seasonNumber,
  playing,
  loading,
  progressPct,
}: {
  seasonNumber: number | null;
  playing: boolean;
  loading: boolean;
  progressPct: number;
}) {
  const SIZE = 64;
  const R = 30;
  const C = 2 * Math.PI * R;
  const dash = (Math.max(0, Math.min(100, progressPct)) / 100) * C;
  const ariaLabel = loading
    ? "Loading episode"
    : playing
      ? "Now playing — open player"
      : "Paused — open player";

  return (
    <>
      <button
        type="button"
        onClick={() => setMiniDrawerOpen(true)}
        aria-label={ariaLabel}
        aria-busy={loading || undefined}
        style={{ background: seasonHex(seasonNumber) }}
        className="absolute top-0 left-1/2 flex size-16 -translate-x-1/2 -translate-y-[25px] cursor-pointer items-center justify-center rounded-full text-white shadow-[0_12px_28px_-6px_rgba(0,0,0,0.45)] transition-transform hover:scale-105 active:scale-95"
      >
        {/* Progress ring starts at 12 o'clock (-90deg rotation). */}
        <svg
          aria-hidden
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="absolute inset-0 size-full -rotate-90"
        >
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            stroke="rgba(255,255,255,0.25)"
            strokeWidth="3"
            fill="none"
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            stroke="white"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${C}`}
            className="transition-[stroke-dasharray] duration-200"
          />
        </svg>

        <span
          aria-hidden
          className="h-7 aspect-291/460 bg-white"
          style={{
            maskImage: "url(/assets/logo/padcast-logo.svg)",
            WebkitMaskImage: "url(/assets/logo/padcast-logo.svg)",
            maskSize: "contain",
            WebkitMaskSize: "contain",
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",
            maskPosition: "center",
            WebkitMaskPosition: "center",
          }}
        />

        <span className="absolute right-0.5 bottom-0.5 flex size-5 items-center justify-center rounded-full bg-white text-purple-05 ring-2 ring-white shadow">
          {loading ? (
            <Loader2 className="size-3 animate-spin" strokeWidth={2.5} />
          ) : playing ? (
            <Pause className="size-3" strokeWidth={2.5} />
          ) : (
            <Play className="ml-0.5 size-3" strokeWidth={2.5} />
          )}
        </span>
      </button>
    </>
  );
}
